import { createLogger } from "../../../../common/createLogger.ts";
import { local } from "./local.ts";
import type { LogEventShowOptions } from "../../../../common/types.ts";
import i18n from "./i18n.ts";
import { notify } from "./notify.ts";
import { toWorker } from "./toWorker.ts";
import { translateEventText } from "./eventText.ts";
import { resolveFeedbackChannel } from "./feedbackTier.ts";
import { emitHighlightCard } from "./highlightCard.ts";
import { emitMoment } from "./momentOverlay.ts";
import { recordAchievementUnlocked } from "./achievementSession.ts";
import { getPhaseMomentSpec } from "../../../../common/phaseMoment.ts";

const saveEvent = () => {
	throw new Error("UI events should not be saved to DB");
};

// Title label for a highlight (L2) card, keyed by event type. Only `draft` is
// wired this lane; other types fall back to a generic label until G1a-2 wires
// their events.
// Title label per event `type` for the server-emitted highlight cards. Signing /
// trade highlights are emitted UI-side via emitHighlightCard with their own
// title (feedbackHeadline.ts), so they are not routed through here.
const HIGHLIGHT_TITLE_KEYS: Record<string, { key: string; en: string }> = {
	draft: { key: "feedbackHighlight.draftTitle", en: "New draft pick" },
	playerFeat: { key: "feedbackHighlight.featTitle", en: "Record performance" },
	playoffs: { key: "feedbackHighlight.playoffTitle", en: "Playoff milestone" },
};

const highlightTitle = (type: string) => {
	const entry = HIGHLIGHT_TITLE_KEYS[type];
	if (entry) {
		return i18n.t(entry.key, { ns: "ui", defaultValue: entry.en });
	}
	return i18n.t("feedbackHighlight.genericTitle", {
		ns: "ui",
		defaultValue: "Highlight",
	});
};

const momentTitle = (variant: "celebration" | "somber") =>
	variant === "somber"
		? i18n.t("feedbackMoment.somberTitle", {
				ns: "ui",
				defaultValue: "A tough moment",
			})
		: i18n.t("feedbackMoment.celebrationTitle", {
				ns: "ui",
				defaultValue: "What a moment!",
			});

// Localize a redirect's CTA label (textKey → translated, falling back to the
// English text). Shared by moment CTAs (phase / celebration / somber).
const ctaLabel = (redirect: LogEventShowOptions["redirect"]) => {
	if (!redirect) {
		return undefined;
	}
	return redirect.textKey
		? i18n.t(redirect.textKey, { ns: "ui", defaultValue: redirect.text })
		: redirect.text;
};

// Phase cutscenes must never fire on top of a running live game.
const inLiveGame = () =>
	window.location.pathname.includes("/live_game") &&
	local.getState().liveGameInProgress;

// Route a highlight/moment-tier event to its channel. Toast keeps the legacy
// snackbar path below (untouched). `redirect.url` becomes the card / CTA target.
const routeNonToastEvent = (
	channel: "highlight" | "moment",
	options: LogEventShowOptions,
) => {
	const message = translateEventText({
		text: options.text,
		textKey: options.textKey,
		textParams: options.textParams,
	});

	if (channel === "highlight") {
		emitHighlightCard({
			title: highlightTitle(options.type),
			message,
			htmlIsSafe: options.htmlIsSafe,
			url: options.redirect?.url,
		});
		return;
	}

	// Phase-transition cutscene (plan G1c): "phase" variant, title keyed by the
	// server-declared phase, no sim lock, optional auto-advance.
	if (options.phaseMoment) {
		const spec = getPhaseMomentSpec(options.phaseMoment.phase);
		emitMoment({
			variant: "phase",
			title: spec
				? i18n.t(spec.titleKey, { ns: "ui", defaultValue: spec.titleText })
				: momentTitle("celebration"),
			subtitle: message,
			subtitleHtmlIsSafe: options.htmlIsSafe,
			ctaText: ctaLabel(options.redirect),
			ctaUrl: options.redirect?.url,
			autoAdvance: options.phaseMoment.autoAdvance,
		});
		return;
	}

	const variant =
		options.extraClass === "notification-danger" ? "somber" : "celebration";
	emitMoment({
		variant,
		title: momentTitle(variant),
		subtitle: message,
		subtitleHtmlIsSafe: options.htmlIsSafe,
		ctaText: ctaLabel(options.redirect),
		ctaUrl: options.redirect?.url,
		// Celebration moments (championship) may carry team colors for the
		// background + confetti; somber moments ignore them (fall back to dark).
		teamColors:
			variant === "celebration" ? options.momentTeamColors : undefined,
	});
};

export const showEvent = (options: LogEventShowOptions) => {
	// G7-1: count achievement unlocks for the session-scoped home prompt. Done
	// before any channel routing / live-game suppression so the count is accurate
	// even when the toast itself is not shown.
	if (options.type === "achievement") {
		recordAchievementUnlocked();
	}

	const channel = resolveFeedbackChannel(options.tier);
	if (channel !== "toast") {
		// hideInLiveGame semantics for phase cutscenes (celebration/somber moments
		// still fire — a championship should not be swallowed by a live game).
		if (options.phaseMoment && inLiveGame()) {
			return;
		}
		routeNonToastEvent(channel, options);
		return;
	}

	const {
		hideInLiveGame,
		htmlIsSafe,
		onClose,
		persistent,
		redirect,
		text,
		textKey,
		textParams,
		type,
	} = options;
	// extraClass is mutated below (persistent → notification-danger default).
	let extraClass = options.extraClass;
	let title;

	if (type === "gameWon" || type === "gameLost" || type === "gameTied") {
		if (local.getState().showLeagueTopBar) {
			return;
		}
	}

	if (type === "error") {
		title = i18n.t("notifications.error", {
			ns: "ui",
			defaultValue: "Error!",
		});
	} else if (type === "changes") {
		title = i18n.t("notifications.changesSinceLastVisit", {
			ns: "ui",
			defaultValue: "Changes since your last visit",
		});
	} else if (type === "healedList") {
		title = i18n.t("notifications.recoveredFromInjury", {
			ns: "ui",
			defaultValue: "Recovered from injury",
		});
	} else if (type === "injuredList") {
		title = i18n.t("notifications.injuredThisGame", {
			ns: "ui",
			defaultValue: "Injured this game",
		});
	}

	if (persistent && extraClass === undefined) {
		extraClass = "notification-danger";
	}

	let showNotification = true;

	// Don't show non-critical notification if we're viewing a live game now. The additional liveGameInProgress check handles the case when an error occurs before the live game starts (such as roster size) and that should still be displayed
	if (
		(hideInLiveGame || !persistent) &&
		window.location.pathname.includes("/live_game")
	) {
		const liveGameInProgress = local.getState().liveGameInProgress;
		if (liveGameInProgress) {
			showNotification = false;
		}
	}

	let finalText = translateEventText({ text, textKey, textParams });
	let finalHtmlIsSafe = htmlIsSafe;
	if (redirect) {
		const linkText = redirect.textKey
			? i18n.t(redirect.textKey, { ns: "ui", defaultValue: redirect.text })
			: redirect.text;
		const escapedUrl = redirect.url.replace(/"/g, "&quot;");
		finalText = `<a href="${escapedUrl}">${linkText}</a>`;
		finalHtmlIsSafe = true;
	}

	if (showNotification) {
		notify(finalText, title, {
			extraClass,
			htmlIsSafe: finalHtmlIsSafe,
			onClose,
			persistent,
		});

		// Persistent notifications are very rare and should stop game sim when displayed. Run async for performance
		if (persistent) {
			toWorker("main", "getLocal", "autoPlayUntil").then((autoPlayUntil) => {
				if (!autoPlayUntil) {
					toWorker("main", "lockSet", ["stopGameSim", true]);
				}
			});
		}
	}
};

export const logEvent = createLogger(saveEvent, showEvent);
